import { ContextMenuHandler, type IMenuItem } from "@asup/context-menu";
import React from "react";
import { useArsListing } from "./ArsListingContextProvider";
import { getScaffoldContextMenuTarget } from "./getScaffoldContextMenuTarget";
import {
  ARSL_ADD_ROW,
  ARSL_ALIGN_TEXT,
  ARSL_REMOVE_COLUMN,
  ARSL_REMOVE_ROW,
  ARSL_TOGGLE_VARIABLE_LIST,
  type ArsListingColumn,
  type ArsTextAlignment,
  HEADER_ROW_ID,
} from "./interface";

interface ArsListingContextMenuProviderProps<T> {
  children: React.ReactNode;
  selectedColumns: ArsListingColumn<T>[];
}

/**
 * Wraps ArsListing scaffold content and provides context-menu behaviour.
 *
 * @param props - Component props
 * @param props.children - Child scaffold content rendered inside context-menu handler
 * @param props.selectedColumns - Current selected listing columns
 * @returns Context-menu wrapper for listing content
 */
export const ArsListingContextMenuProvider = <T,>({
  children,
  selectedColumns,
}: ArsListingContextMenuProviderProps<T>): React.ReactElement => {
  const { state, dispatch } = useArsListing<T>();
  const [contextMenuTarget, setContextMenuTarget] = React.useState<
    { colId: string; rowId: string } | undefined
  >(undefined);

  /**
   * Captures scaffold row and column ids before opening the context menu.
   *
   * @param event - Context menu event fired from scaffold cells
   */
  const handleScaffoldContextMenuCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    setContextMenuTarget(getScaffoldContextMenuTarget(event.target));
  };

  /**
   * Dispatches text-alignment changes for the selected context-menu column.
   *
   * @param alignment - Desired text alignment
   * @param isHeader - Whether alignment applies to header cells
   */
  const handleChangeTextAlignment = (alignment: ArsTextAlignment, isHeader: boolean): void => {
    /* istanbul ignore else */
    if (state && !state.settings.disabled && contextMenuTarget) {
      dispatch({
        operation: ARSL_ALIGN_TEXT,
        colId: contextMenuTarget.colId,
        alignment,
        isHeader,
      });
    }
  };

  /**
   * Adds a new row after the currently targeted context-menu row.
   *
   * @param ellipsis - Whether the inserted row is an ellipsis row
   */
  const handleAddRow = (ellipsis: boolean): void => {
    /* istanbul ignore else */
    if (state && !state.settings.disabled && contextMenuTarget) {
      dispatch({
        operation: ARSL_ADD_ROW,
        rowId: contextMenuTarget.rowId,
        type: ellipsis ? "ellipsis" : "default",
      });
    }
  };

  /**
   * Removes the currently targeted body row when allowed.
   */
  const handleRemoveRow = (): void => {
    /* istanbul ignore else */
    if (
      state &&
      !state.settings.disabled &&
      contextMenuTarget &&
      state.bodyData.arsBodyRows.length > 1
    ) {
      const bodyRowIndex = state.bodyData.arsBodyRows.findIndex(
        (row) => row.id === contextMenuTarget.rowId,
      );
      /* istanbul ignore else */
      if (bodyRowIndex !== -1) {
        dispatch({
          operation: ARSL_REMOVE_ROW,
          rowId: contextMenuTarget.rowId,
        });
      }
    }
  };

  /**
   * Removes the currently targeted column when allowed.
   */
  const handleRemoveColumn = (): void => {
    /* istanbul ignore else */
    if (state && !state.settings.disabled && contextMenuTarget && selectedColumns.length > 1) {
      dispatch({
        operation: ARSL_REMOVE_COLUMN,
        colId: contextMenuTarget.colId,
      });
    }
  };

  /**
   * Toggles visibility of the variable-list sidebar.
   */
  const handleToggleVariableList = (): void => {
    /* istanbul ignore else */
    if (state) {
      dispatch({ operation: ARSL_TOGGLE_VARIABLE_LIST });
    }
  };

  const isHeaderRow = contextMenuTarget?.rowId === HEADER_ROW_ID;
  const isRowActionDisabled =
    state?.settings.disabled || contextMenuTarget === undefined || isHeaderRow;

  const arslContextMenuItems: IMenuItem[] = [
    {
      label: "Justify text",
      disabled: state?.settings.disabled || contextMenuTarget === undefined,
      group: [
        {
          label: "Left",
          action: () => handleChangeTextAlignment("left", isHeaderRow),
        },
        {
          label: "Centre",
          action: () => handleChangeTextAlignment("center", isHeaderRow),
        },
        {
          label: "Right",
          action: () => handleChangeTextAlignment("right", isHeaderRow),
        },
      ],
    },
    {
      label: "Add row",
      disabled: isRowActionDisabled,
      group: [
        {
          label: "Display",
          action: () => handleAddRow(false),
        },
        {
          label: "Ellipsis",
          action: () => handleAddRow(true),
        },
      ],
    },
    {
      label: "Remove row",
      action: handleRemoveRow,
      disabled:
        isRowActionDisabled ||
        (state?.bodyData.arsBodyRows.length ??
          // istanbul ignore next
          0) <= 1,
    },
    {
      label: "Remove column",
      action: handleRemoveColumn,
      disabled:
        state?.settings.disabled || contextMenuTarget === undefined || selectedColumns.length <= 1,
    },
    {
      label: state?.settings.showVariableList ? "Hide variable list" : "Show variable list",
      action: handleToggleVariableList,
      disabled: contextMenuTarget === undefined,
    },
  ];

  return (
    <ContextMenuHandler
      menuItems={arslContextMenuItems}
      onContextMenuCapture={handleScaffoldContextMenuCapture}
    >
      {children}
    </ContextMenuHandler>
  );
};

ArsListingContextMenuProvider.displayName = "ArsListingContextMenuProvider";
